import {
    Box,
    Typography,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    alpha,
} from "@mui/material";
import { ReactNode, useCallback, useRef, useMemo } from "react";
import { GridConfig, FieldConfig, ColumnGroupLabel, ColumnLabel } from "../types/formConfig";

interface FormColumnarSectionProps {
    title: string;
    showDivider?: boolean;
    columnLabels: (string | ColumnLabel)[];
    columnGroupLabels?: ColumnGroupLabel[];
    rowLabelHeader?: string;
    rowLabelColumnWidth?: string | number;
    rowGroupHeader?: string;
    rowGroupColumnWidth?: string | number;
    columnWidths?: (string | number | undefined)[];
    columnGroupWidths?: (string | number | undefined)[];
    grids: GridConfig[];
    renderField: (fieldConfig: FieldConfig) => ReactNode;
    showOnlyImportant?: boolean;
    renderRowLabel?: (grid: GridConfig, gridIndex: number) => ReactNode | undefined;
    renderRowGroupLabel?: (grid: GridConfig, gridIndex: number) => ReactNode | undefined;
    maxFixedColumns?: number; // Beyond this many columns, enable horizontal scroll (default: no limit)
}

/**
 * Renders a section with columnar layout (like Schedule E Part One)
 * where rows represent field types (e.g., "Rents received") and
 * columns represent properties (A, B, C)
 *
 * Grids without rowLabel are rendered as regular grids above the table.
 */
const FormColumnarSection = ({
    title,
    showDivider = false,
    columnLabels,
    columnGroupLabels,
    rowLabelHeader,
    rowLabelColumnWidth,
    rowGroupHeader,
    rowGroupColumnWidth,
    columnWidths,
    columnGroupWidths,
    grids,
    renderField,
    showOnlyImportant = false,
    renderRowLabel,
    renderRowGroupLabel,
    maxFixedColumns,
}: FormColumnarSectionProps) => {
    const hasRowGroupColumn = rowGroupHeader !== undefined;
    const theme = useTheme();

    // Normalize columnLabels into {label, span} — strings default to span=1
    const normalizedColumnLabels: ColumnLabel[] = useMemo(
        () => columnLabels.map((c) => (typeof c === "string" ? { label: c, span: 1 } : { ...c, span: c.span ?? 1 })),
        [columnLabels],
    );

    // Total underlying data-column slots (sum of spans). Each slot is one column in <colgroup>.
    const totalDataColumns = useMemo(
        () => normalizedColumnLabels.reduce((acc, c) => acc + (c.span ?? 1), 0),
        [normalizedColumnLabels],
    );

    // Separate grids into header, columnar (table rows), and footer
    const { headerGrids, columnarGrids, footerGrids } = useMemo(() => {
        const header: GridConfig[] = [];
        const columnar: GridConfig[] = [];
        const footer: GridConfig[] = [];

        grids.forEach((grid) => {
            if (grid.rowLabel || grid.skipRowLabel) {
                // Has rowLabel OR is a continuation row (covered by a previous rowSpan) = table row
                columnar.push(grid);
            } else if (grid.position === "footer") {
                footer.push(grid);
            } else {
                // Default to header for non-columnar grids without position
                header.push(grid);
            }
        });

        return { headerGrids: header, columnarGrids: columnar, footerGrids: footer };
    }, [grids]);

    // Row-level importance: a row is "important" when the grid config declares it
    // (via `important` or `fieldImportantTemplate`), OR when any field in the row
    // is marked important — so a single important cell lights up its row header
    // symmetrically with how it lights up its column header.
    const isRowImportant = (grid: GridConfig): boolean => {
        if (grid.important || grid.fieldImportantTemplate) return true;
        if (grid.fields?.some((f) => f && f.important)) return true;
        if (grid.rowFieldTemplates?.some((t) => t.important)) return true;
        return false;
    };

    const importantRowIndices = useMemo(() => {
        const set = new Set<number>();
        columnarGrids.forEach((grid, idx) => {
            if (isRowImportant(grid)) set.add(idx);
        });
        return set;
    }, [columnarGrids]);

    // Column-level importance: a column slot is "important" when any field in that slot is
    // marked important, or when a per-column template declares it. Includes fields from
    // rows that are themselves marked important — a column header should reflect any
    // important cell beneath it regardless of whether the row label is also accented.
    const importantColSet = useMemo(() => {
        const set = new Set<number>();
        columnarGrids.forEach((grid) => {
            // rowLabelSpan absorbs (rowLabelSpan - 1) leading data columns into the row-label
            // cell; the row's fields describe only the trailing slots. Shift the slot counter
            // so per-column importance detection lines up with how rows actually render.
            const rowLabelSpan = Math.max(1, grid.rowLabelSpan ?? 1);
            let slot = rowLabelSpan - 1;
            grid.fields.forEach((field) => {
                const span = Math.max(1, field.colSpan ?? 1);
                if (field.important) {
                    for (let s = 0; s < span; s++) set.add(slot + s);
                }
                slot += span;
            });
            if (grid.rowFieldTemplates) {
                grid.rowFieldTemplates.forEach((tpl, i) => {
                    if (tpl.important) set.add(i + (rowLabelSpan - 1));
                });
            }
        });
        return set;
    }, [columnarGrids]);

    // showOnlyImportant rules:
    //   - filter columns down to those marked important (when any exist)
    //   - filter rows down to those marked important (when any exist)
    //   - both filters apply simultaneously — only important × important cells render
    //   - neither → leave the table as-is (outer FormRenderer decides whether to hide the section)
    const hasImportantCols = importantColSet.size > 0;
    const hasImportantRows = importantRowIndices.size > 0;
    const shouldFilterCols = showOnlyImportant && hasImportantCols;
    const shouldFilterRows = showOnlyImportant && hasImportantRows;

    const visibleColIndices = useMemo(() => {
        if (!shouldFilterCols) return Array.from({ length: totalDataColumns }, (_, i) => i);
        return Array.from({ length: totalDataColumns }, (_, i) => i).filter((i) => importantColSet.has(i));
    }, [shouldFilterCols, importantColSet, totalDataColumns]);

    const visibleColumnarGrids = useMemo(() => {
        if (!shouldFilterRows) return columnarGrids;
        return columnarGrids.filter((_, idx) => importantRowIndices.has(idx));
    }, [shouldFilterRows, columnarGrids, importantRowIndices]);

    // Pre-compute: which visible col slots are inside a merged group
    const mergedColSlots = useMemo(() => {
        const set = new Set<number>();
        if (!columnGroupLabels) return set;
        let col = 0;
        columnGroupLabels.forEach((group) => {
            if (group.merged) {
                for (let s = 0; s < group.span; s++) set.add(col + s);
            }
            col += group.span;
        });
        return set;
    }, [columnGroupLabels]);

    // Filter column labels by the visible data-column slots they cover, preserving spans.
    // Also track which (visible) slots each label covers so headers can pick up the "important"
    // color when any underlying slot is marked important, and whether a label sits entirely
    // inside a merged column-group (so it should span both header rows).
    const filteredColumnLabels = useMemo(() => {
        const result: (ColumnLabel & { important?: boolean; merged?: boolean })[] = [];
        let slot = 0;
        normalizedColumnLabels.forEach((c) => {
            const span = c.span ?? 1;
            const visibleSlotsForLabel: number[] = [];
            for (let s = 0; s < span; s++) {
                if (visibleColIndices.includes(slot + s)) visibleSlotsForLabel.push(slot + s);
            }
            if (visibleSlotsForLabel.length > 0) {
                const important = visibleSlotsForLabel.some((si) => importantColSet.has(si));
                const merged = visibleSlotsForLabel.every((si) => mergedColSlots.has(si));
                result.push({ label: c.label, span: visibleSlotsForLabel.length, important, merged });
            }
            slot += span;
        });
        return result;
    }, [normalizedColumnLabels, visibleColIndices, importantColSet, mergedColSlots]);

    // Filter columnGroupLabels to match the actual visible data columns by expanding spans into
    // per-column entries, keeping only the visible indices, and re-collapsing same-label runs.
    // This keeps the group header's colSpan in sync even when the underlying column count is
    // dynamic (linkedArrayField) or when showOnlyImportant has hidden some columns.
    const filteredColumnGroupLabels = useMemo(() => {
        if (!columnGroupLabels) return undefined;

        // Expand: assign each column index to its group label
        const colToGroup: { label: string; colIndex: number; merged?: boolean }[] = [];
        let col = 0;
        columnGroupLabels.forEach((group) => {
            for (let s = 0; s < group.span; s++) {
                colToGroup.push({ label: group.label, colIndex: col, merged: group.merged });
                col++;
            }
        });

        // Clamp to the visible column slots
        const visibleGroups = visibleColIndices.map((i) => colToGroup[i]).filter(Boolean);
        if (visibleGroups.length === 0) return undefined;

        const collapsed: ColumnGroupLabel[] = [];
        visibleGroups.forEach((g) => {
            const last = collapsed[collapsed.length - 1];
            if (last && last.label === g.label && last.merged === g.merged) {
                last.span += 1;
            } else {
                collapsed.push({ label: g.label, span: 1, merged: g.merged });
            }
        });

        // If the total expanded span was smaller than the visible count (e.g. columnGroupLabels
        // covered fewer columns than columnLabels), pad by stretching the last group so its
        // combined colSpan matches the rendered data columns.
        const covered = collapsed.reduce((acc, g) => acc + g.span, 0);
        if (covered < visibleColIndices.length && collapsed.length > 0) {
            collapsed[collapsed.length - 1].span += visibleColIndices.length - covered;
        }
        return collapsed;
    }, [columnGroupLabels, visibleColIndices]);

    // Map each filtered group index to the filteredColumnLabel indices it covers. Used when a
    // group is `merged` so we can render those column labels in the group row with rowSpan=2.
    const groupToLabelIndices = useMemo(() => {
        if (!filteredColumnGroupLabels) return [] as number[][];
        const result: number[][] = [];
        let labelIdx = 0;
        filteredColumnGroupLabels.forEach((g) => {
            const indices: number[] = [];
            let covered = 0;
            while (covered < g.span && labelIdx < filteredColumnLabels.length) {
                const lbl = filteredColumnLabels[labelIdx];
                indices.push(labelIdx);
                covered += lbl.span ?? 1;
                labelIdx += 1;
            }
            result.push(indices);
        });
        return result;
    }, [filteredColumnGroupLabels, filteredColumnLabels]);

    // Total visible data columns (sum of filtered label spans)
    const visibleDataColumnCount = useMemo(
        () => filteredColumnLabels.reduce((acc, c) => acc + (c.span ?? 1), 0),
        [filteredColumnLabels],
    );

    const isScrollable = maxFixedColumns !== undefined && visibleDataColumnCount > maxFixedColumns;
    const topScrollRef = useRef<HTMLDivElement>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const syncing = useRef(false);

    const handleTopScroll = useCallback(() => {
        if (syncing.current) return;
        syncing.current = true;
        if (tableContainerRef.current && topScrollRef.current) {
            tableContainerRef.current.scrollLeft = topScrollRef.current.scrollLeft;
        }
        syncing.current = false;
    }, []);

    const handleTableScroll = useCallback(() => {
        if (syncing.current) return;
        syncing.current = true;
        if (topScrollRef.current && tableContainerRef.current) {
            topScrollRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
        }
        syncing.current = false;
    }, []);

    const tableMinWidth = isScrollable ? `${200 + visibleDataColumnCount * 120}px` : undefined;

    const isFullWidthTitle =
        filteredColumnGroupLabels &&
        filteredColumnGroupLabels.length === 1 &&
        filteredColumnGroupLabels[0].span === visibleDataColumnCount;

    const isMultiGroup = filteredColumnGroupLabels && !isFullWidthTitle;

    return (
        <Box>
            {showDivider && <Divider sx={{ my: 2 }} />}
            {title && (
                <Typography
                    variant="subtitle1"
                    sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                >
                    {title}
                </Typography>
            )}

            {/* Render header grids (before the columnar table) */}
            {headerGrids.map((grid, gridIndex) => (
                <Box
                    key={`header-${gridIndex}`}
                    sx={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${grid.columns || 2}, 1fr)`,
                        gap: grid.gap || 2,
                        mb: 2,
                        ...grid.sx,
                    }}
                >
                    {grid.fields.map((field) => renderField(field))}
                </Box>
            ))}

            {/* Render columnar table */}
            {visibleColumnarGrids.length > 0 && (
                <>
                    {isScrollable && (
                        <Box
                            ref={topScrollRef}
                            onScroll={handleTopScroll}
                            sx={{ overflowX: "scroll", overflowY: "hidden", mb: -1 }}
                        >
                            <Box sx={{ height: 12, minWidth: tableMinWidth }} />
                        </Box>
                    )}
                    <TableContainer
                        component={Paper}
                        variant="outlined"
                        ref={tableContainerRef}
                        onScroll={isScrollable ? handleTableScroll : undefined}
                        sx={{
                            backgroundColor: alpha(theme.palette.background.paper, 0.5),
                            ...(isScrollable && { overflowX: "scroll" }),
                        }}
                    >
                        <Table
                            size="small"
                            sx={{
                                tableLayout: "fixed",
                                width: isScrollable ? "auto" : "100%",
                                minWidth: tableMinWidth,
                            }}
                        >
                            <colgroup>
                                {hasRowGroupColumn && (
                                    <col
                                        style={{
                                            width: isScrollable ? 80 : rowGroupColumnWidth ?? "8%",
                                        }}
                                    />
                                )}
                                <col
                                    style={{
                                        width: isScrollable
                                            ? 200
                                            : rowLabelColumnWidth ?? "25%",
                                    }}
                                />
                                {visibleColIndices.map((colIdx, i) => {
                                    // columnWidths takes precedence; otherwise derive from columnGroupWidths if set
                                    let override = columnWidths?.[colIdx];
                                    if (override == null && columnGroupWidths && columnGroupLabels) {
                                        let runningCol = 0;
                                        for (let g = 0; g < columnGroupLabels.length; g++) {
                                            const { span } = columnGroupLabels[g];
                                            if (colIdx >= runningCol && colIdx < runningCol + span) {
                                                const groupW = columnGroupWidths[g];
                                                if (typeof groupW === "string" && groupW.endsWith("%")) {
                                                    const pct = parseFloat(groupW);
                                                    if (!Number.isNaN(pct)) override = `${pct / span}%`;
                                                } else if (typeof groupW === "number") {
                                                    override = groupW / span;
                                                }
                                                break;
                                            }
                                            runningCol += span;
                                        }
                                    }
                                    if (isScrollable) {
                                        return (
                                            <col
                                                key={i}
                                                style={{ minWidth: override ?? 120 }}
                                            />
                                        );
                                    }
                                    return (
                                        <col
                                            key={i}
                                            style={override != null ? { width: override } : undefined}
                                        />
                                    );
                                })}
                            </colgroup>
                            <TableHead>
                                {/* Full-width title row when a single group spans all columns */}
                                {isFullWidthTitle && (
                                <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.12) }}>
                                    <TableCell
                                        align="center"
                                        colSpan={visibleDataColumnCount + 1 + (hasRowGroupColumn ? 1 : 0)}
                                        sx={{
                                            fontWeight: 700,
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                        }}
                                    >
                                        {filteredColumnGroupLabels![0].label}
                                    </TableCell>
                                </TableRow>
                            )}
                                {/* Multi-group header row (e.g. "Beginning of tax year" | "End of tax year") */}
                                {isMultiGroup && (
                                <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.12) }}>
                                    <TableCell
                                        rowSpan={2}
                                        colSpan={hasRowGroupColumn ? 2 : 1}
                                        sx={{
                                            fontWeight: 700,
                                            borderRight: `1px solid ${theme.palette.divider}`,
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        {rowLabelHeader ?? rowGroupHeader ?? ""}
                                    </TableCell>
                                    {filteredColumnGroupLabels!.flatMap((group, gIdx) => {
                                        if (group.merged) {
                                            // Render each covered column label in the group row
                                            // with rowSpan=2 — effectively merging that column's
                                            // group+label cells into one.
                                            return (groupToLabelIndices[gIdx] || []).map((li) => {
                                                const col = filteredColumnLabels[li];
                                                return (
                                                    <TableCell
                                                        key={`merged-grp-${gIdx}-${li}`}
                                                        align="center"
                                                        rowSpan={2}
                                                        colSpan={col.span ?? 1}
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: "0.75rem",
                                                            lineHeight: 1.3,
                                                            px: 0.5,
                                                            py: 1,
                                                            borderRight: `1px solid ${theme.palette.divider}`,
                                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                                            "&:last-child": { borderRight: 0 },
                                                            ...(col.important && {
                                                                color: theme.palette.mode === "dark" ? "#ffb74d" : "#f57c00",
                                                            }),
                                                        }}
                                                    >
                                                        {col.label}
                                                    </TableCell>
                                                );
                                            });
                                        }
                                        return [
                                            <TableCell
                                                key={`grp-${gIdx}-${group.label}`}
                                                align="center"
                                                colSpan={group.span}
                                                sx={{
                                                    fontWeight: 700,
                                                    borderRight: `1px solid ${theme.palette.divider}`,
                                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                                    "&:last-child": { borderRight: 0 },
                                                }}
                                            >
                                                {group.label}
                                            </TableCell>,
                                        ];
                                    })}
                                </TableRow>
                            )}
                                {/* Column label row */}
                                <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.08) }}>
                                    {!isMultiGroup && (
                                    <TableCell
                                        colSpan={hasRowGroupColumn ? 2 : 1}
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "0.75rem",
                                            lineHeight: 1.3,
                                            px: 1,
                                            py: 1,
                                            borderRight: `1px solid ${theme.palette.divider}`,
                                        }}
                                    >
                                        {rowLabelHeader ?? rowGroupHeader ?? ""}
                                    </TableCell>
                                )}
                                    {filteredColumnLabels.map((col, i) => {
                                        // When the column is inside a merged group, its header is
                                        // already rendered (rowSpan=2) in the group row above.
                                        if (col.merged) return null;
                                        return (
                                            <TableCell
                                            key={`${col.label}-${i}`}
                                            align="center"
                                            colSpan={col.span ?? 1}
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: "0.75rem",
                                                lineHeight: 1.3,
                                                px: 0.5,
                                                py: 1,
                                                borderRight: `1px solid ${theme.palette.divider}`,
                                                "&:last-child": { borderRight: 0 },
                                                ...(col.important && {
                                                    color: theme.palette.mode === "dark" ? "#ffb74d" : "#f57c00",
                                                }),
                                            }}
                                        >
                                                {col.label}
                                            </TableCell>
                                        );
                                })}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleColumnarGrids.map((grid, gridIndex) => {
                                    const mergeWithGroup = !!(grid.mergeWithRowGroupCell && hasRowGroupColumn);
                                    const groupCell = hasRowGroupColumn && !grid.skipRowGroupCell && !mergeWithGroup ? (
                                        <TableCell
                                            rowSpan={grid.rowGroupSpan ?? 1}
                                            sx={{
                                                fontWeight: 600,
                                                borderRight: `1px solid ${theme.palette.divider}`,
                                                verticalAlign: "middle",
                                                textAlign: "center",
                                                py: 1,
                                                px: 0.5,
                                            }}
                                        >
                                            {renderRowGroupLabel?.(grid, gridIndex) ?? (grid.rowGroupLabel ?? "")}
                                        </TableCell>
                                    ) : null;
                                    // fullRowSpan: single cell across row-label + all data columns. If a
                                    // separate group cell was rendered for this row, the full-span cell
                                    // excludes the group column; otherwise it absorbs it too.
                                    if (grid.fullRowSpan) {
                                        const firstField = grid.fields.find((f) => f && !f.empty);
                                        const fullSpan = visibleDataColumnCount + 1 + (hasRowGroupColumn && groupCell === null ? 1 : 0);
                                        return (
                                            <TableRow key={gridIndex}>
                                                {groupCell}
                                                <TableCell
                                                    colSpan={fullSpan}
                                                    sx={{
                                                        borderRight: 0,
                                                        py: 1.5,
                                                        px: 1,
                                                        ...(isRowImportant(grid) && {
                                                            color: theme.palette.mode === "dark" ? "#ffb74d" : "#f57c00",
                                                        }),
                                                    }}
                                                >
                                                    {renderRowLabel?.(grid, gridIndex) ?? (
                                                        firstField ? renderField(firstField) : <Box>{grid.rowLabel || ""}</Box>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }
                                    // mergedDataColumns: row-label column + single cell across all data columns
                                    if (grid.mergedDataColumns) {
                                        const firstField = grid.fields.find((f) => f && !f.empty);
                                        return (
                                            <TableRow key={gridIndex}>
                                                {groupCell}
                                                {!grid.skipRowLabel && (
                                                    <TableCell
                                                        component="th"
                                                        scope="row"
                                                        rowSpan={grid.rowSpan}
                                                        sx={{
                                                            fontWeight: 500,
                                                            borderRight: `1px solid ${theme.palette.divider}`,
                                                            verticalAlign: "top",
                                                            py: 1.5,
                                                            ...(isRowImportant(grid) && {
                                                                color: theme.palette.mode === "dark" ? "#ffb74d" : "#f57c00",
                                                            }),
                                                        }}
                                                    >
                                                        {renderRowLabel?.(grid, gridIndex) ?? (
                                                            <Box>
                                                                <Box>{grid.rowLabel || ""}</Box>
                                                                {grid.rowLabelSubFields && grid.rowLabelSubFields.length > 0 && (
                                                                    <Box sx={{ mt: 0.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                                                                        {grid.rowLabelSubFields.map((f, i) => (
                                                                            <Box key={f.fieldPath || i}>{renderField(f)}</Box>
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                )}
                                                <TableCell
                                                    colSpan={visibleDataColumnCount}
                                                    sx={{
                                                        py: 1,
                                                        px: 1,
                                                        verticalAlign: "center",
                                                        ...(!firstField
                                                            ? { backgroundColor: alpha(theme.palette.action.disabled, 0.08) }
                                                            : {}),
                                                    }}
                                                >
                                                    {firstField && renderField(firstField)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }
                                    // rowLabelSpan lets the row-label cell absorb (rowLabelSpan - 1) leading
                                    // data columns. The fields array then describes only the trailing columns.
                                    // mergeWithRowGroupCell additionally extends the row-label cell one column
                                    // to the left so it absorbs the outer row-group column.
                                    // When columns are filtered (showOnlyImportant), only count the absorbed
                                    // *visible* data columns so the row-label colSpan and trailing-fields slice
                                    // line up with the actually-rendered cells.
                                    const rowLabelSpan = Math.max(1, grid.rowLabelSpan ?? 1);
                                    const absorbedDataCols = rowLabelSpan - 1;
                                    const visibleAbsorbedDataCols = visibleColIndices.filter((i) => i < absorbedDataCols).length;
                                    const rowLabelColSpan = 1 + visibleAbsorbedDataCols + (mergeWithGroup ? 1 : 0);
                                    return (
                                        <TableRow
                                    key={gridIndex}
                                    sx={{}}
                                >
                                            {groupCell}
                                            {!grid.skipRowLabel && (
                                            <TableCell
                                            component="th"
                                            scope="row"
                                            rowSpan={grid.rowSpan}
                                            colSpan={rowLabelColSpan}
                                            sx={{
                                                fontWeight: 500,
                                                borderRight: `1px solid ${theme.palette.divider}`,
                                                verticalAlign: "top",
                                                py: 1.5,
                                                // Color the row label with the important-field accent when the
                                                // row is marked important — either at the grid level, via a per-
                                                // template flag, or because any field in the row is important.
                                                ...(isRowImportant(grid) && {
                                                    color: theme.palette.mode === "dark" ? "#ffb74d" : "#f57c00",
                                                }),
                                            }}
                                        >
                                                {renderRowLabel?.(grid, gridIndex) ?? (
                                                <Box>
                                                    <Box>{grid.rowLabel || ""}</Box>
                                                    {grid.rowLabelSubFields && grid.rowLabelSubFields.length > 0 && (
                                                        <Box sx={{ mt: 0.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                                                            {grid.rowLabelSubFields.map((f, i) => (
                                                                <Box key={f.fieldPath || i}>{renderField(f)}</Box>
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                            </TableCell>
                                    )}
                                            {(() => {
                                        // Expand grid.fields into per-slot entries honoring each field's colSpan.
                                        // A field with colSpan N occupies N underlying data-column slots; only
                                        // the first slot renders a cell (with colSpan=N), the rest are skipped.
                                        // When rowLabelSpan > 1, shift the fields' slots so they occupy the
                                        // trailing columns (the leading ones were absorbed by the row-label cell).
                                        const slotToField: { field?: FieldConfig; spanStart: boolean; span: number }[] = [];
                                        let slot = absorbedDataCols;
                                        grid.fields.forEach((f) => {
                                            const span = Math.max(1, f.colSpan ?? 1);
                                            for (let s = 0; s < span; s++) {
                                                slotToField[slot + s] = { field: f, spanStart: s === 0, span };
                                            }
                                            slot += span;
                                        });
                                        for (let s = slot; s < totalDataColumns; s++) slotToField[s] = { spanStart: true, span: 1 };

                                        return visibleColIndices.slice(visibleAbsorbedDataCols).map((colIdx) => {
                                        const entry = slotToField[colIdx] ?? { spanStart: true, span: 1 };
                                        if (!entry.spanStart) return null;
                                        const { field } = entry;
                                        // When the cell's span extends beyond the visible region (due to hidden columns),
                                        // clamp the rendered colSpan to the number of visible slots covered.
                                        let renderedSpan = 1;
                                        if (entry.span > 1) {
                                            let covered = 0;
                                            for (let s = 0; s < entry.span; s++) {
                                                if (visibleColIndices.includes(colIdx + s)) covered += 1;
                                            }
                                            renderedSpan = Math.max(1, covered);
                                        }
                                        return (
                                            <TableCell
                                                key={colIdx}
                                                align="center"
                                                colSpan={renderedSpan}
                                                sx={{
                                                    borderRight: `1px solid ${theme.palette.divider}`,
                                                    "&:last-child": { borderRight: 0 },
                                                    py: 1,
                                                    px: 1,
                                                    verticalAlign: "center",
                                                    ...(!field || field.empty
                                                        ? {
                                                              backgroundColor: alpha(
                                                                  theme.palette.action.disabled,
                                                                  0.08,
                                                              ),
                                                          }
                                                        : {}),
                                                }}
                                            >
                                                {field && !field.empty && field.static && (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontSize: "0.85rem",
                                                            fontWeight: 700,
                                                            fontStyle: "italic",
                                                            color: theme.palette.text.primary,
                                                        }}
                                                    >
                                                        {field.static}
                                                    </Typography>
                                                )}
                                                {field && !field.empty && !field.static && (
                                                    <Box
                                                        sx={{
                                                            "& .MuiTextField-root": { width: "100%" },
                                                            "& .MuiInputBase-input": {
                                                                fontSize: "0.75rem",
                                                                px: 1,
                                                                textOverflow: "ellipsis",
                                                                overflow: "hidden",
                                                                whiteSpace: "nowrap",
                                                                "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                                                                    WebkitAppearance: "none",
                                                                    margin: 0,
                                                                },
                                                                MozAppearance: "textfield",
                                                            },
                                                            "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                                                        }}
                                                    >
                                                        {renderField({
                                                            ...field,
                                                            label: field.label ?? "",
                                                        } as FieldConfig)}
                                                    </Box>
                                                )}
                                            </TableCell>
                                        );
                                    });
                                    })()}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            {/* Render footer grids (after the columnar table) */}
            {footerGrids.map((grid, gridIndex) => (
                <Box
                    key={`footer-${gridIndex}`}
                    sx={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${grid.columns || 2}, 1fr)`,
                        gap: grid.gap || 2,
                        mt: 2,
                        ...grid.sx,
                    }}
                >
                    {grid.fields.map((field) => renderField(field))}
                </Box>
            ))}
        </Box>
    );
};

export default FormColumnarSection;
